import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import FormModal from "../billing/FormModal";
import { Topic } from "../../features/training/models/Programme";
import { TOPIC_KINDS, TopicKind } from "../../features/training/contracts/TopicKind";
import { SaveTopicRequest } from "../../features/training/contracts/SaveTopicRequest";
import { addTopic, updateTopic } from "../../features/training/slice-extensions/training-slice-extension";

interface TopicFormDialogProps {
  programmeId: string;
  /** The topic to edit; undefined adds a new one after the last. */
  topic?: Topic;
  nextSortOrder: number;
  onClose: () => void;
}

const numberField = (value: string): number => (value.trim() === "" ? NaN : Number(value));

/**
 * Adds a topic to a programme or edits one; the server checks the code is free. A new topic
 * left without a code gets the next free "T<n>" from the server; an edit keeps a code.
 */
const TopicFormDialog: React.FC<TopicFormDialogProps> = ({ programmeId, topic, nextSortOrder, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [code, setCode] = useState(topic?.code ?? "");
  const [title, setTitle] = useState(topic?.title ?? "");
  const [kind, setKind] = useState<TopicKind>(topic?.kind ?? "Theory");
  const [unitMinutes, setUnitMinutes] = useState(String(topic?.unitMinutes ?? 90));
  const [requiredCount, setRequiredCount] = useState(String(topic?.requiredCount ?? 1));
  const [sortOrder, setSortOrder] = useState(String(topic?.sortOrder ?? nextSortOrder));
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const request: SaveTopicRequest = {
      code: code.trim() === "" ? null : code.trim(),
      title: title.trim(),
      kind,
      unitMinutes: numberField(unitMinutes),
      requiredCount: numberField(requiredCount),
      sortOrder: numberField(sortOrder),
    };
    setSaving(true);
    setError(null);
    const result = topic
      ? await dispatch(updateTopic({ programmeId, topicId: topic.id, request }))
      : await dispatch(addTopic({ programmeId, request }));
    setSaving(false);
    if (result.meta.requestStatus === "fulfilled") {
      onClose();
    } else {
      const payload = result.payload as { message?: string } | undefined;
      setError(payload?.message ?? t("training.topics.saveFailed"));
    }
  };

  const field = "input w-full";
  return (
    <FormModal onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-4 lg:p-6">
        <h3 className="mb-4">{topic ? t("training.topics.edit") : t("training.topics.add")}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="label">
            <span className="label-text">{t("training.topics.code")}</span>
            <input className={field} value={code} maxLength={12} required={topic !== undefined}
              placeholder={topic ? undefined : t("training.topics.codeAuto")} onChange={(e) => setCode(e.target.value)} />
          </label>
          <label className="label">
            <span className="label-text">{t("training.topics.kindLabel")}</span>
            <select className={field} value={kind} onChange={(e) => setKind(e.target.value as TopicKind)}>
              {TOPIC_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`training.topics.kind.${k}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="label sm:col-span-2">
            <span className="label-text">{t("training.topics.topicTitle")}</span>
            <input className={field} value={title} maxLength={150} required onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="label">
            <span className="label-text">{t("training.topics.unitMinutes")}</span>
            <input className={field} type="number" min={15} max={240} required value={unitMinutes}
              onChange={(e) => setUnitMinutes(e.target.value)} />
          </label>
          <label className="label">
            <span className="label-text">{t("training.topics.requiredCount")}</span>
            <input className={field} type="number" min={0} max={50} required value={requiredCount}
              onChange={(e) => setRequiredCount(e.target.value)} />
          </label>
          <label className="label">
            <span className="label-text">{t("training.topics.sortOrder")}</span>
            <input className={field} type="number" required value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)} />
          </label>
        </div>
        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {t("common.cancel", "Cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {t("training.topics.save")}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default TopicFormDialog;
